/**
 * UPT-2079 L14a (web §5.2, rest §4.5 et §4.6) — MODÈLE PUR du panneau « Habillage » : lecture et écriture du bloc `presentation` d'un brouillon, rapport de
 * contraste (les six paires du rest §4.6 n° 3, ratio avant correction, « AA » ou couleur de correction), thème généré pour l'aperçu, et MIROIR TYPESCRIPT de
 * l'extracteur du serveur (`presentationFromSummary`, rest §4.5) qu'utilise le dépôt de démonstration. Aucune couleur d'état n'existe dans une palette.
 * Fonctions pures, sans DOM ; n'importe rien de `prototypes/` (types du contrat seulement).
 */
import { safeComponentRecipes } from '@/uptk/core/theme/component_recipes';
import type { PrototypeStyleSummary, ResolvedModuleTheme } from '../data/contracts';
import type {
  ModuleSpecAsset,
  ModuleSpecFontRef,
  ModuleSpecPalette,
  ModuleSpecPresentation,
  ValidationIssue,
} from '../data/moduleSpecContracts';
import { MODULE_SPEC_V12_LIMITS } from '../data/moduleSpecContracts';
import { resolveMeasuredFont } from '@/uptk/core/theme/module_fonts';
import {
  BLACK,
  CONTRAST_PAIRS,
  WHITE,
  buildModeColors,
  correctModeColors,
  displayRatio,
  luminanceOf,
  resolveModuleThemeColors,
  type ContrastPairId,
  type GeneratedModuleTheme,
  type ModuleThemeColors,
  type ModuleThemeScheme,
} from '@/uptk/core/theme/module_theme';
import { contrast_ratio } from '@/uptk/core/theme/theme_contrast';

/* ── Lecture / écriture du brouillon ─────────────────────────────────────────────────────────────────────────────── */

type Json = Record<string, unknown>;

const isRecord = (value: unknown): value is Json => typeof value === 'object' && value !== null && !Array.isArray(value);

/** Le bloc `presentation` du document, ou `null`. */
export const readPresentation = (spec: Json): ModuleSpecPresentation | null => {
  const value = spec.presentation;
  return isRecord(value) ? (value as ModuleSpecPresentation) : null;
};

/** Copie du document avec `presentation` remplacé (ou retiré si `null`) ; `specVersion` passe à « 1.2 » (le serveur le ferait de toute façon, remarque `spec.version_bumped`). */
export const writePresentation = <T extends Json>(spec: T, presentation: ModuleSpecPresentation | null): T => {
  const next: Json = { ...spec };
  if (presentation === null) delete next.presentation;
  else {
    next.presentation = presentation;
    if (presentation.skin) next.specVersion = '1.8';
    else if ((presentation.components || presentation.typography?.roles || presentation.typography?.stack) && !['1.7','1.8'].includes(String(next.specVersion))) next.specVersion = '1.6';
    if (next.specVersion === '1.0' || next.specVersion === '1.1') next.specVersion = '1.2';
  }
  return next as T;
};

/** Valeurs par défaut du schéma v1.2 (`$defs.presentation`). */
export const PRESENTATION_DEFAULTS = {
  origin: 'manual',
  baseScheme: 'light',
  applyTo: 'all',
  shape: { radius: 'md', shadow: 'sm', density: 'comfortable', cardStyle: 'outlined' },
} as const;

export const RADIUS_STEPS = ['none', 'sm', 'md', 'lg', 'xl'] as const;
export const SHADOW_STEPS = ['none', 'sm', 'md', 'lg'] as const;
export const DENSITY_STEPS = ['compact', 'comfortable', 'spacious'] as const;
export const CARD_STYLES = ['flat', 'outlined', 'elevated', 'glass'] as const;

/** `#RGB` ou `#RRGGBB` (casse libre) → `#rrggbb` minuscule ; `null` sinon (la normalisation du serveur ferait de même, rest §4.1 n° 4). */
export const normalizeHexInput = (value: string): string | null => {
  const text = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(text)) return text;
  if (/^#[0-9a-f]{3}$/.test(text)) return `#${text[1]}${text[1]}${text[2]}${text[2]}${text[3]}${text[3]}`;
  return null;
};

/* ── Thème généré, contraste ─────────────────────────────────────────────────────────────────────────────────────── */

const validPalette = (palette: ModuleSpecPalette | undefined): ModuleSpecPalette | null =>
  palette && normalizeHexInput(palette.accent ?? '') ? palette : null;

/** Thème d'un module pour l'aperçu (`ModuleThemeScope`) : même résolution que le générateur (rest §4.6). */
export const toGeneratedTheme = (moduleCode: string, presentation: ModuleSpecPresentation | null | undefined): GeneratedModuleTheme => {
  const p = presentation ?? {};
  const baseScheme: ModuleThemeScheme = p.baseScheme === 'dark' ? 'dark' : 'light';
  const resolved = resolveModuleThemeColors({ baseScheme, palette: validPalette(p.palette), paletteAlt: validPalette(p.paletteAlt) });
  const shape = p.shape ?? {};
  return {
    version: 1,
    moduleCode,
    applyTo: p.applyTo === 'custom-pages' ? 'custom-pages' : 'all',
    baseScheme,
    light: resolved.light,
    dark: resolved.dark,
    components: p.components,
    typography: {
      stack: p.typography?.stack,
      roles: p.typography?.roles,
      ...(p.typography?.heading ? { heading: { family: p.typography.heading.family, weights: [...(p.typography.heading.weights ?? [400, 700])] } } : {}),
      ...(p.typography?.body ? { body: { family: p.typography.body.family, weights: [...(p.typography.body.weights ?? [400, 700])] } } : {}),
    },
    shape: {
      radius: shape.radius ?? PRESENTATION_DEFAULTS.shape.radius,
      shadow: shape.shadow ?? PRESENTATION_DEFAULTS.shape.shadow,
      density: shape.density ?? PRESENTATION_DEFAULTS.shape.density,
      cardStyle: shape.cardStyle ?? PRESENTATION_DEFAULTS.shape.cardStyle,
    },
    adjustments: resolved.adjustments,
  };
};

/** Forme `ResolvedModuleTheme` du contrat (les deux modes et les corrections nommées). */
export const resolvePresentation = (presentation: ModuleSpecPresentation | null | undefined): ResolvedModuleTheme => {
  const theme = toGeneratedTheme('module', presentation);
  return { light: theme.light, dark: theme.dark, adjustments: theme.adjustments };
};

export interface ContrastRow {
  scheme: ModuleThemeScheme;
  pair: ContrastPairId;
  /** Contraste AVANT correction, arrondi à deux décimales. */
  ratio: number;
  min: number;
  ok: boolean;
  /** Couleur de correction (hexadécimal) quand la paire échoue ; `null` sinon. */
  fixedTo: string | null;
}

const SLOT_OF_PAIR: Readonly<Record<ContrastPairId, readonly string[]>> = {
  'ink/surface': ['ink'],
  'ink/surfaceRaised': ['ink'],
  'inkMuted/surface': ['inkMuted'],
  'inkMuted/surfaceRaised': ['inkMuted'],
  'accent/surface': ['accent'],
  'onAccent/accent': ['onAccent', 'accent'],
};

/**
 * Rapport de contraste (rest §4.6 n° 3) : pour chaque mode et chaque paire, le ratio de la palette TELLE QUE SAISIE (valeurs Géode pour les clés absentes) et la
 * couleur de correction que poserait le générateur. Les corrections viennent de `correctModeColors` : la règle est unique, le serveur applique la même.
 */
export const checkPairs = (presentation: ModuleSpecPresentation | null | undefined): ContrastRow[] => {
  const p = presentation ?? {};
  const base: ModuleThemeScheme = p.baseScheme === 'dark' ? 'dark' : 'light';
  const palette = validPalette(p.palette);
  const alt = validPalette(p.paletteAlt);
  const rawOf = (scheme: ModuleThemeScheme): ModuleThemeColors => {
    if (scheme === base) return buildModeColors(scheme, palette);
    if (alt) return buildModeColors(scheme, alt);
    return buildModeColors(scheme, palette ? { accent: palette.accent } : null);
  };
  const rows: ContrastRow[] = [];
  // Un mode sans palette garde le thème Géode tel quel (aligné sur le rest, décision du CORE du 20.10) : aucune paire contrôlée pour ce mode.
  const hasPalette = (scheme: ModuleThemeScheme): boolean => (scheme === base ? Boolean(palette) : Boolean(alt ?? palette));
  (['light', 'dark'] as const).forEach((scheme) => {
    if (!hasPalette(scheme)) return;
    const colors = rawOf(scheme);
    const { adjustments } = correctModeColors(scheme, colors);
    for (const pair of CONTRAST_PAIRS) {
      const value = contrast_ratio(colors[pair.a], colors[pair.b]) ?? 1;
      const ok = value >= pair.min;
      let fixedTo: string | null = null;
      if (!ok) {
        const matches = adjustments.filter((adjustment) => SLOT_OF_PAIR[pair.id].includes(adjustment.slot));
        fixedTo = matches.length > 0 ? matches[matches.length - 1].to : null;
      }
      rows.push({ scheme, pair: pair.id, ratio: displayRatio(value), min: pair.min, ok, fixedTo });
    }
  });
  return rows;
};

/** Contraste de deux couleurs hexadécimales (jetons d'état calculés sur la surface du module : avertissement < 4,5), arrondi à deux décimales ; `null` si illisible. */
export const ratioBetween = (a: string, b: string): number | null => {
  const value = contrast_ratio(a, b);
  return value === null ? null : displayRatio(value);
};

/* ── Ressources ──────────────────────────────────────────────────────────────────────────────────────────────────── */

const ASSET_KEY = /^[a-z][a-z0-9-]{1,40}$/;

/** Clé proposée pour une ressource : le texte ramené au motif `^[a-z][a-z0-9-]{1,40}$`, rendu unique parmi `taken`. */
export const proposeAssetKey = (seed: string | null | undefined, taken: ReadonlySet<string>, fallbackIndex: number): string => {
  const cleaned = (seed ?? '')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 36)
    .replace(/-+$/g, '');
  let base = /^[a-z]/.test(cleaned) && cleaned.length >= 2 ? cleaned : `image-${fallbackIndex}`;
  if (!ASSET_KEY.test(base)) base = `image-${fallbackIndex}`;
  let key = base;
  let n = 2;
  while (taken.has(key)) {
    key = `${base.slice(0, 36)}-${n}`;
    n += 1;
  }
  return key;
};

export const isAssetKey = (value: string): boolean => ASSET_KEY.test(value);

export const totalAssetBytes = (assets: readonly ModuleSpecAsset[] | undefined): number =>
  (assets ?? []).reduce((sum, asset) => sum + (Number.isFinite(asset.bytes) ? asset.bytes : 0), 0);

/** Vrai si une ressource de plus tiendrait dans les bornes du Module Spec (16 ressources, 2 Mio au total). */
export const canAddAsset = (assets: readonly ModuleSpecAsset[] | undefined, bytes: number): boolean =>
  (assets ?? []).length < MODULE_SPEC_V12_LIMITS.assetsPerSpec &&
  totalAssetBytes(assets) + bytes <= MODULE_SPEC_V12_LIMITS.assetsTotalMaxBytes;

/* ── Extracteur (miroir de `ModulePresentationExtractor`, rest §4.5) ─────────────────────────────────────────────── */

const rgbOf = (hex: string): [number, number, number] => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];

const hslOf = (hex: string): { h: number; s: number; l: number } => {
  const [r, g, b] = rgbOf(hex).map((channel) => channel / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: (h * 60 + 360) % 360, s, l };
};

const cr = (a: string, b: string): number => contrast_ratio(a, b) ?? 1;

const issue = (code: string, path: string, message = ''): ValidationIssue => ({ code, path, message });

const radiusOf = (px: number | null): NonNullable<ModuleSpecPresentation['shape']>['radius'] => {
  if (px === null) return 'md';
  if (px < 1) return 'none';
  if (px < 5) return 'sm';
  if (px < 11) return 'md';
  if (px < 19) return 'lg';
  return 'xl';
};

const shadowOf = (share: number, blur: number | null): NonNullable<ModuleSpecPresentation['shape']>['shadow'] => {
  if (share < 0.25 || blur === null) return 'none';
  if (blur <= 4) return 'sm';
  if (blur <= 12) return 'md';
  return 'lg';
};

const densityOf = (px: number | null): NonNullable<ModuleSpecPresentation['shape']>['density'] => {
  if (px === null) return 'comfortable';
  if (px < 12) return 'compact';
  if (px <= 24) return 'comfortable';
  return 'spacious';
};

const fontRefOf = (
  entries: PrototypeStyleSummary['fonts']['heading'],
  path: string,
  remarks: ValidationIssue[]
): ModuleSpecFontRef | null => {
  const first = entries[0];
  if (!first) return null;
  const { key, fallback } = resolveMeasuredFont(first.family);
  if (fallback) remarks.push(issue('presentation.font_fallback', path, first.family));
  // Poids mesurés de la famille retenue : toutes les entrées qui se résolvent vers la MÊME clé (« Segoe UI » et « Arial » donnent `system`).
  const weights = [
    ...new Set(
      entries
        .filter((entry) => resolveMeasuredFont(entry.family).key === key)
        .map((entry) => Math.min(700, Math.max(400, Math.round(entry.weight / 100) * 100)))
    ),
  ].sort((a, b) => a - b);
  return { family: key, weights: (weights.length > 0 ? weights : [400, 700]) as ModuleSpecFontRef['weights'] };
};

export interface ExtractedPresentation {
  presentation: ModuleSpecPresentation | null;
  remarks: ValidationIssue[];
}

/**
 * Miroir TypeScript de `ModulePresentationExtractor.extract` (rest §4.5) : fonction pure et déterministe qui tire le bloc `presentation` du résumé de style.
 * `assets` n'est jamais posé (geste du Maker). Les remarques `presentation.*` sont des remarques (`validation.infos`), jamais des erreurs.
 */
export const presentationFromSummary = (summary: PrototypeStyleSummary): ExtractedPresentation => {
  const remarks: ValidationIssue[] = [];
  if (summary.backgrounds.length === 0) {
    return { presentation: null, remarks: [issue('presentation.no_style', '/presentation')] };
  }
  const surface = summary.backgrounds[0].color;
  const baseScheme: 'light' | 'dark' = summary.scheme ?? (luminanceOf(surface) < 0.18 ? 'dark' : 'light');

  const cardBackground = summary.cards.background;
  const surfaceRaised = cardBackground && cr(cardBackground, surface) >= 1.04 ? cardBackground : undefined;

  const texts = summary.texts;
  let inkIndex = texts.findIndex((entry) => cr(entry.color, surface) >= 4.5);
  let ink: string | undefined;
  if (inkIndex >= 0) {
    ink = texts[inkIndex].color;
  } else if (texts.length > 0) {
    inkIndex = texts.reduce((best, entry, index) => (cr(entry.color, surface) > cr(texts[best].color, surface) ? index : best), 0);
    ink = texts[inkIndex].color;
    remarks.push(issue('presentation.contrast', '/presentation/palette/ink', 'ink/surface'));
  }
  let inkMuted: string | undefined;
  if (ink !== undefined) {
    const inkColor = ink;
    const mutedIndex = texts.findIndex(
      (entry, index) => index !== inkIndex && cr(entry.color, surface) >= 4.5 && cr(entry.color, inkColor) >= 1.25
    );
    if (mutedIndex >= 0) inkMuted = texts[mutedIndex].color;
  }

  const candidates = [...summary.accents, ...summary.borders, ...summary.backgrounds.slice(1)].map((entry) => entry.color);
  const accent = candidates.find((color) => {
    const hsl = hslOf(color);
    const luminance = luminanceOf(color);
    return (
      hsl.s >= 0.25 &&
      luminance >= 0.12 &&
      luminance <= 0.85 &&
      cr(color, surface) >= 1.8 &&
      (ink === undefined || cr(color, ink) >= 1.5)
    );
  });

  let palette: ModuleSpecPalette | undefined;
  if (accent === undefined) {
    remarks.push(issue('presentation.no_accent', '/presentation/palette'));
  } else {
    const hsl = hslOf(accent);
    if ((hsl.h >= 345 || hsl.h <= 20) && hsl.s >= 0.5) {
      remarks.push(issue('presentation.accent_state_like', '/presentation/palette/accent'));
    }
    const border = summary.borders[0] && cr(summary.borders[0].color, surface) >= 1.15 ? summary.borders[0].color : undefined;
    palette = {
      accent,
      onAccent: cr(BLACK, accent) >= cr(WHITE, accent) ? BLACK : WHITE,
      surface,
      ...(surfaceRaised ? { surfaceRaised } : {}),
      ...(ink ? { ink } : {}),
      ...(inkMuted ? { inkMuted } : {}),
      ...(border ? { border } : {}),
    };
  }

  const heading = fontRefOf(summary.fonts.heading, '/presentation/typography/heading', remarks);
  const body = fontRefOf(summary.fonts.body, '/presentation/typography/body', remarks);

  const shadow = shadowOf(summary.cards.shadowShare, summary.cards.shadowBlurPx);
  const cardStyle: NonNullable<ModuleSpecPresentation['shape']>['cardStyle'] =
    summary.cards.translucentShare >= 0.5
      ? 'glass'
      : shadow === 'md' || shadow === 'lg'
        ? 'elevated'
        : summary.cards.borderedShare >= 0.5
          ? 'outlined'
          : 'flat';

  const presentation: ModuleSpecPresentation = {
    origin: 'prototype',
    extractor: 'style-probe@1',
    ...(summary.components ? {components: safeComponentRecipes(summary.components)} : {}),
    baseScheme,
    ...(palette ? { palette } : {}),
    ...(heading || body ? { typography: { ...(heading ? { heading } : {}), ...(body ? { body } : {}) } } : {}),
    shape: {
      radius: radiusOf(summary.cards.radiusPx),
      shadow,
      density: densityOf(summary.cards.paddingPx),
      cardStyle,
    },
    applyTo: 'all',
  };
  const tokens = summary.customProperties?.[baseScheme];
  if (tokens?.accent) presentation.palette = { ...presentation.palette, ...tokens, accent: tokens.accent };
  const alternate = summary.customProperties?.[baseScheme === 'light' ? 'dark' : 'light'];
  if (alternate?.accent) presentation.paletteAlt = { ...alternate, accent: alternate.accent };
  if (summary.typography) presentation.typography = { ...presentation.typography, ...summary.typography };
  return { presentation, remarks };
};
